import { ReactElement, useRef, useState } from 'react';

import PhotoGalleryBlock from 'components/UI/PhotoGalleryBlock';
import RichText from '../UI/Richtext';
import { MAX_SCREEN_WIDTH } from 'lib/consts';
import { Asset } from 'contentful';
import type { Document } from '@contentful/rich-text-types';

interface PhotoGallertProps {
  heading?: string | undefined;
  copy?: Document | undefined;
  backgroundColor?: 'white' | 'muted' | undefined;
  imagesPerRow: number;
  gallery: PhotoBlockProps[];
}

export interface PhotoBlockProps {
  image: Asset;
  rowOneText?: string | undefined;
  rowTwoText?: string | undefined;
  rowThreeText?: string | undefined;
  link?: string | undefined;
  openInNewTab?: boolean | undefined;
}

const ITEMS_PER_PAGE = 25;

function slowScrollToElement(element: HTMLDivElement): void {
  const startY = window.scrollY;
  const targetY = element.getBoundingClientRect().top + window.scrollY;
  const distance = targetY - startY;
  const duration = 900;
  let startTime: number | null = null;

  const easeInOutCubic = (progress: number): number =>
    progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

  const step = (timestamp: number): void => {
    if (startTime === null) startTime = timestamp;

    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);

    window.scrollTo(0, startY + distance * easedProgress);

    if (progress < 1) {
      window.requestAnimationFrame(step);
    }
  };

  window.requestAnimationFrame(step);
}

function getPaginationItems(
  currentPage: number,
  totalPages: number
): Array<number | 'ellipsis-left' | 'ellipsis-right'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-right', totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      'ellipsis-left',
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    'ellipsis-left',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'ellipsis-right',
    totalPages,
  ];
}

export default function PhotoGallery({
  heading,
  gallery,
  copy,
  backgroundColor = 'white',
  imagesPerRow,
}: PhotoGallertProps): ReactElement {
  const galleryRef = useRef<HTMLDivElement>(null);
  const maxImageWidth = Math.round(MAX_SCREEN_WIDTH / imagesPerRow);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(gallery.length / ITEMS_PER_PAGE));
  const paginationItems = getPaginationItems(currentPage, totalPages);
  const paginatedGallery = gallery.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number): void => {
    if (page === currentPage) return;

    setCurrentPage(page);
    if (galleryRef.current) {
      slowScrollToElement(galleryRef.current);
    }
  };

  const backgroundClass = backgroundColor === 'muted' ? 'bg-muted' : 'bg-white';

  return (
    <div
      ref={galleryRef}
      className={`px-site-component ecp-my text-primary-actual ${backgroundClass} ${
        backgroundColor === 'white' ? 'pb-10 md:pb-20' : 'py-10 md:py-20'
      }`}
    >
      <div className="flex items-stretch">
        <div>
          {heading && (
            <h2 className="bordered-heading font-heading text-new-34 lg:text-4xl lg:leading-new-43">
              {heading}
            </h2>
          )}
          {copy && <RichText document={copy} classNames="my-4" />}
        </div>
      </div>
      <div className="flex flex-wrap mt-4 -mx-2">
        {paginatedGallery.map((image, i) => (
          <PhotoGalleryBlock key={i} {...image} width={imagesPerRow} query={`w=${maxImageWidth}`} />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-3 text-secondary-actual">
          <button
            type="button"
            aria-label="Previous gallery page"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex h-8 w-8 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="9"
              height="15"
              viewBox="0 0 9 15"
              fill="none"
            >
              <path
                d="M7.5 1.5L1.5 7.5L7.5 13.5"
                stroke="#003B49"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {paginationItems.map((item) => {
            if (typeof item !== 'number') {
              return (
                <span
                  key={item}
                  aria-hidden="true"
                  className="flex h-10 min-w-4 items-center justify-center"
                >
                  ...
                </span>
              );
            }

            const isActive = item === currentPage;

            return (
              <button
                key={item}
                type="button"
                aria-label={`Gallery page ${item}`}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => handlePageChange(item)}
                className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-heading ${
                  isActive
                    ? 'border-transparent bg-secondary text-white'
                    : 'border-secondary bg-white text-primary-actual'
                }`}
              >
                {item}
              </button>
            );
          })}

          <button
            type="button"
            aria-label="Next gallery page"
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="flex h-8 w-8 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="9"
              height="15"
              viewBox="0 0 9 15"
              fill="none"
            >
              <path
                d="M1.5 1.5L7.5 7.5L1.5 13.5"
                stroke="#003B49"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
